import { useEffect } from 'react';
import { useI18n } from '../i18n';
import './DifficultySheet.css';

interface Props {
  open: boolean;
  mode: 'solo' | 'ranked';
  closeLabel: string;
  onPick: (difficulty: number) => void;
  onClose: () => void;
}

const LEVELS = [
  { d: 1, accent: '#6B8C68' },
  { d: 2, accent: '#5B7FA6' },
  { d: 3, accent: '#7C6FCB' },
  { d: 4, accent: '#B68A4E' },
  { d: 5, accent: '#B6614E' },
] as const;

export function DifficultySheet({ open, mode, closeLabel, onPick, onClose }: Props) {
  const { t } = useI18n();

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  if (!open) return null;

  const title = mode === 'ranked'
    ? t.home.rankedMenuSolo
    : t.home.funMenuSolo;
  const subtitle = mode === 'ranked'
    ? t.game.rankedSoloHint
    : t.game.chooseDifficulty;

  return (
    <div
      className={`diff-sheet diff-sheet--${mode}`}
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div className="diff-sheet-card" onClick={e => e.stopPropagation()}>
        <button type="button" className="diff-sheet-close" onClick={onClose} aria-label={closeLabel}>×</button>
        <h2 className="diff-sheet-title">
          {mode === 'ranked' && <span className="diff-sheet-trophy" aria-hidden="true">🏆</span>}
          {title}
        </h2>
        <p className="diff-sheet-sub">{subtitle}</p>
        <div className="diff-sheet-list">
          {LEVELS.map(lv => (
            <button
              key={lv.d}
              type="button"
              className="diff-sheet-item"
              style={{ ['--accent' as any]: lv.accent }}
              onClick={() => { onPick(lv.d); onClose(); }}
            >
              <span className="diff-sheet-item-bars" aria-hidden="true">
                {[1, 2, 3, 4, 5].map(i => (
                  <span
                    key={i}
                    className={`diff-sheet-bar diff-sheet-bar--${i} ${i <= lv.d ? 'is-on' : ''}`}
                  />
                ))}
              </span>
              <span className="diff-sheet-item-text">
                <span className="diff-sheet-item-title">{(t.difficulties as any)[lv.d]}</span>
                <span className="diff-sheet-item-meta" aria-hidden="true" />
              </span>
              <span className="diff-sheet-item-arr" aria-hidden="true">→</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
